import type { PrismaClient } from '@prisma/client';
import Decimal from 'decimal.js';
import { formatMoney } from '@roovers/shared';
import { HttpError } from '../../core/errors.js';

type Render = (model: never) => Promise<Buffer>;

const fmtDate = (d: Date | null | undefined) => {
  if (!d) return '';
  const [y, m, day] = d.toISOString().slice(0, 10).split('-');
  return `${day}/${m}/${y}`;
};

const partyLines = (
  p: { tin: string | null; address: string | null; city: string | null; country: string } | null,
) =>
  [
    p?.address,
    [p?.city, p?.country].filter(Boolean).join(', '),
    p?.tin ? `TIN: ${p.tin}` : null,
  ].filter(Boolean) as string[];

/** PDFs for purchase orders (sent to the vendor) and payout receipts (proof of payment). */
export async function renderPurchasePdf(
  db: PrismaClient,
  companyId: number,
  kind: 'purchaseorder' | 'payout',
  id: number,
  deps: { co: unknown; renderPdf: unknown },
): Promise<{ pdf: Buffer; filename: string }> {
  const render = deps.renderPdf as Render;
  const co = deps.co;
  if (kind === 'purchaseorder') {
    const o = await db.purchaseOrder.findFirst({
      where: { id, companyId },
      include: { lines: { orderBy: { lineNo: 'asc' } } },
    });
    if (!o) throw new HttpError(404, 'NOT_FOUND', 'Purchase order not found');
    const party = await db.party.findUnique({ where: { id: o.partyId } });
    const money = (v: Decimal.Value | { toString(): string }) =>
      formatMoney(v.toString(), o.currency);
    const meta: [string, string][] = [['Order date', fmtDate(o.issueDate)]];
    if (o.expectedDate) meta.push(['Expected delivery', fmtDate(o.expectedDate)]);
    if (o.reference) meta.push(['Reference', o.reference]);
    if (o.currency !== 'UGX')
      meta.push([
        'Exchange rate',
        `1 ${o.currency} = UGX ${new Decimal(o.exchangeRate.toString())}`,
      ]);
    const totals: [string, string][] = [['Subtotal', money(o.subtotal)]];
    if (new Decimal(o.discountTotal.toString()).gt(0))
      totals.push(['Discounts included', money(o.discountTotal)]);
    totals.push(['VAT', money(o.taxTotal)], [`Total ${o.currency}`, money(o.total)]);
    const number = o.number ?? 'DRAFT';
    const pdf = await render({
      company: co,
      title: 'Purchase order',
      number,
      statusLabel:
        o.status === 'cancelled' ? 'CANCELLED' : o.status === 'draft' ? 'DRAFT' : undefined,
      partyLabel: 'VENDOR',
      meta,
      billTo: { name: party?.name ?? '', lines: partyLines(party) },
      lines: o.lines.map((l) => ({
        description: l.description,
        qty: new Decimal(l.quantity.toString()).toString(),
        unit: l.unit ?? '',
        price: money(l.unitPrice),
        discount:
          l.discountType === 'percent'
            ? `${new Decimal(l.discountValue.toString())}%`
            : l.discountType === 'amount'
              ? money(l.discountValue)
              : '',
        vat: `${new Decimal(l.vatRate.toString())}%`,
        total: money(l.lineNet),
      })),
      totals,
      notes: o.notes,
      terms: o.terms,
      showPayment: false,
    } as never);
    return { pdf, filename: `${number}.pdf` };
  }

  const p = await db.payoutReceipt.findFirst({
    where: { id, companyId },
    include: { allocations: true },
  });
  if (!p) throw new HttpError(404, 'NOT_FOUND', 'Payout not found');
  const party = await db.party.findUnique({ where: { id: p.partyId } });
  const account = await db.financialAccount.findUnique({ where: { id: p.financialAccountId } });
  const bills = await db.vendorBill.findMany({
    where: {
      id: { in: p.allocations.filter((a) => a.targetType === 'bill').map((a) => a.targetId) },
    },
    select: { id: true, number: true, vendorRef: true },
  });
  const exps = await db.expense.findMany({
    where: {
      id: { in: p.allocations.filter((a) => a.targetType === 'expense').map((a) => a.targetId) },
    },
    select: { id: true, number: true },
  });
  const money = (v: Decimal.Value | { toString(): string }) =>
    formatMoney(v.toString(), p.currency);
  const pdf = await render({
    company: co,
    title: 'Payout receipt',
    number: p.payoutNo,
    statusLabel: p.status === 'void' ? 'VOID' : undefined,
    partyLabel: 'PAID TO',
    meta: [
      ['Date', fmtDate(p.payoutDate)],
      ['Paid from', account?.name ?? ''],
      ['Reference', p.reference ?? ''],
    ],
    billTo: { name: party?.name ?? '', lines: partyLines(party) },
    lines: p.allocations.map((a) => {
      const b = a.targetType === 'bill' ? bills.find((x) => x.id === a.targetId) : null;
      const e = a.targetType === 'expense' ? exps.find((x) => x.id === a.targetId) : null;
      return {
        description: b
          ? `Payment of bill ${b.number ?? ''}${b.vendorRef ? ` (your invoice ${b.vendorRef})` : ''}`
          : `Reimbursement ${e?.number ?? ''}`,
        qty: '1',
        unit: '',
        price: money(a.amount),
        discount: '',
        vat: '',
        total: money(a.amount),
      };
    }),
    totals: [
      ...(new Decimal(p.whtWithheld.toString()).gt(0)
        ? ([['Tax withheld (WHT)', money(p.whtWithheld)]] as [string, string][])
        : []),
      ['Total paid', money(p.amount)],
    ],
    notes: p.notes,
    terms: null,
    showPayment: false,
  } as never);
  return { pdf, filename: `${p.payoutNo}.pdf` };
}
